import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { EditRubricGeneralInfoSection } from 'apps/sigma2-ui/src/components/rubrics/EditRubricGeneralInfoSection';
import { EditRubricJobOpeningSection } from 'apps/sigma2-ui/src/components/rubrics/EditRubricJobOpeningSection';
import { EditRubricCategoriesAndCriteriaSection } from 'apps/sigma2-ui/src/components/rubrics/EditRubricCategoriesAndCriteriaSection';
import { EditRubricAddCategoryStrippedSection } from 'apps/sigma2-ui/src/components/rubrics/EditRubricAddCategoryStrippedSection';
import { EditRubricViewHeader } from 'apps/sigma2-ui/src/components/rubrics/EditRubricViewHeader';
import { EditableRubricCategoryWithError } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';
import { EditableRubricRoleWithError } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricRoleOperation } from 'libs/end-to-end/src/types/rubrics/rubric';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { EditRubricErrorBanner } from 'apps/sigma2-ui/src/components/rubrics/EditRubricErrorBanner';
import { RubricCategoryModalRowProps } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { RubricRoleModal } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { RubricRoleModalRowProps } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { RubricCategoryModal } from 'apps/sigma2-ui/src/components/rubrics/RubricModals';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';

interface CreateOrEditRubricViewProps {
  onSaveDraft: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onRestore: () => void;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onIntegrationDataSetIdChange: (dataSetId: string) => void;
  onRoleNameChange: (index: number, name: string) => void;
  onRoleOperationChange: (
    index: number,
    operation: RubricRoleOperation,
  ) => void;
  onRoleSave: (index: number) => void;
  onRoleCancel: (index: number) => void;
  onRoleBeginEdit: (index: number) => void;
  onRoleRemove: (index: number) => void;
  onRoleCreate: () => void;
  onRoleSelect: () => void;
  roles: EditableRubricRoleWithError[];
  isRoleLibraryOpen: boolean;
  onDismissRoleLibrary: () => void;
  onAddSelectedRoles: (selectedRoles: string[]) => void;
  roleLibraryList: RubricRoleModalRowProps[];
  onCategoryStartEdit: (index: number) => void;
  onCategoryDuplicate: (index: number) => void;
  onCategoryDelete: (index: number) => void;
  onCategoryUpdateLabel: (index: number, label: string) => void;
  onCategoryAddTag: (index: number, newTag: string) => void;
  onCategoryRemoveTag: (index: number, tagToRemove: string) => void;
  onCategoryUpdateCriteria: (
    categoryIndex: number,
    criteriaIndex: number,
    newCriteria: string,
  ) => void;
  onCategoryUpdateWeight: (
    categoryIndex: number,
    criteriaIndex: number,
    newWeight: number,
  ) => void;
  onCategoryRemoveCriteria: (
    categoryIndex: number,
    criteriaIndex: number,
  ) => void;
  onCategoryAddCriteria: (categoryIndex: number) => void;
  onCategoryCreate: () => void;
  onCategorySelect: () => void;
  onCategoryCancel: (index: number) => void;
  onCategorySave: (index: number) => void;
  categories: EditableRubricCategoryWithError[];
  isCategoryLibraryOpen: boolean;
  onDismissCategoryLibrary: () => void;
  onAddSelectedCategories: (selectedCategoryIds: string[]) => void;
  categoryLibraryList: RubricCategoryModalRowProps[];
  gradingType: RubricGradingType;
  possibleTags: string[];
  name: string;
  savedName: string;
  description: string;
  status: RubricStatus;
  integrationDataSetId: string;
  disableSave: boolean;
  possibleIntegrationDataSets: DataSet[];
  createdAt?: Date;
  updatedAt?: Date;
  serverError: string | null;
  isReadOnlyMode: boolean;
  isArchiveMode: boolean;
  canManage: boolean;
}

export const CreateOrEditRubricView: React.FC<CreateOrEditRubricViewProps> = (
  props,
) => {
  const language = useLanguage();

  return (
    <>
      <EditRubricViewHeader
        name={props.savedName}
        gradingType={props.gradingType}
        isPublished={props.status === 'published'}
        onSaveDraft={props.onSaveDraft}
        onPublish={props.onPublish}
        onUnpublish={props.onUnpublish}
        onRestore={props.onRestore}
        disableSave={props.disableSave}
        createdAt={props.createdAt}
        updatedAt={props.updatedAt}
        isReadOnlyMode={props.isReadOnlyMode}
        isArchiveMode={props.isArchiveMode}
        canManage={props.canManage}
      />
      <Spacing
        size="md"
        direction="vertical"
        className="w-full items-center pt-6 pb-16 overflow-visible"
      >
        {props.serverError && (
          <EditRubricErrorBanner
            error={language.rubricServerError({
              code: props.serverError,
              gradingType: props.gradingType,
            })}
          />
        )}
        <EditRubricGeneralInfoSection
          gradingType={props.gradingType}
          name={props.name}
          description={props.description}
          integrationDataSetId={props.integrationDataSetId}
          possibleIntegrationDataSets={props.possibleIntegrationDataSets}
          onChangeName={props.onNameChange}
          onChangeDescription={props.onDescriptionChange}
          onChangeIntegrationDataSetId={props.onIntegrationDataSetIdChange}
          isReadOnlyMode={props.isReadOnlyMode}
        />
        <EditRubricJobOpeningSection
          roles={props.roles}
          onRoleCancel={props.onRoleCancel}
          onRoleSave={props.onRoleSave}
          onRoleNameChange={props.onRoleNameChange}
          onRoleOperationChange={props.onRoleOperationChange}
          onRoleBeginEdit={props.onRoleBeginEdit}
          onRoleRemove={props.onRoleRemove}
          onRoleCreate={props.onRoleCreate}
          onRoleSelect={props.onRoleSelect}
          isReadOnlyMode={props.isReadOnlyMode}
        />
        <EditRubricCategoriesAndCriteriaSection
          gradingType={props.gradingType}
          categories={props.categories}
          onCategoryAddTag={props.onCategoryAddTag}
          onCategoryRemoveTag={props.onCategoryRemoveTag}
          possibleTags={props.possibleTags}
          onCategoryCreate={props.onCategoryCreate}
          onCategorySelect={props.onCategorySelect}
          onCategoryStartEdit={props.onCategoryStartEdit}
          onCategoryDuplicate={props.onCategoryDuplicate}
          onCategoryDelete={props.onCategoryDelete}
          onCategoryRemoveCriteria={props.onCategoryRemoveCriteria}
          onCategoryUpdateCriteria={props.onCategoryUpdateCriteria}
          onCategoryUpdateWeight={props.onCategoryUpdateWeight}
          onCategoryAddCriteria={props.onCategoryAddCriteria}
          onCategoryUpdateLabel={props.onCategoryUpdateLabel}
          onCategoryCancel={props.onCategoryCancel}
          onCategorySave={props.onCategorySave}
          isReadOnlyMode={props.isReadOnlyMode}
        />
        <EditRubricAddCategoryStrippedSection
          gradingType={props.gradingType}
          onCategoryCreate={props.onCategoryCreate}
          onCategorySelect={props.onCategorySelect}
          isReadOnlyMode={props.isReadOnlyMode}
        />
      </Spacing>
      {props.isRoleLibraryOpen && (
        <RubricRoleModal
          roles={props.roleLibraryList}
          onAdd={props.onAddSelectedRoles}
          onDismiss={props.onDismissRoleLibrary}
        />
      )}
      {props.isCategoryLibraryOpen && (
        <RubricCategoryModal
          categories={props.categoryLibraryList}
          onAdd={props.onAddSelectedCategories}
          type={props.gradingType}
          onDismiss={props.onDismissCategoryLibrary}
        />
      )}
    </>
  );
};
